// Waiters for a fetch slot, most wanted first.
//
// The fetcher used to hand slots out first-come-first-served. With a
// hundred tiles loading at once that meant every tile got a trickle of
// its fragments and none finished, so the view stayed empty for minutes
// (SpaceX, 2026-09-16: 26 of 1,377 tiles visible after 90 s).
//
// A waiter carries a priority - lower goes first; the controller ranks a
// fragment by how large it is on screen, with what the camera points at
// ahead of everything - and ties keep their arrival order.
//
// WHAT A WEBSOCKET STREAM WOULD HAVE BOUGHT, OVER HTTP/2. Autodesk's
// viewer re-orders and cancels geometry as the camera moves. Here a
// waiter's priority may be a FUNCTION, re-read by reprioritize() (called
// when the camera moves), and a waiter may say it is no longer wanted
// (isStale - its tile unloaded or was superseded): stale waiters are
// dropped as they reach the front instead of taking a slot. Only work not
// yet on the wire is re-ordered; a request already sent completes.
//
// A binary min-heap: O(log n) push/shift, O(n) reprioritize.
export default class SlotQueue {
  constructor() {
    this._heap = [];
    this._seq = 0;
    this.dropped = 0;
  }

  get length() { return this._heap.length; }

  static _value(p) {
    const n = typeof p === 'function' ? p() : p;
    return Number.isFinite(n) ? n : Number.MAX_SAFE_INTEGER;
  }

  /**
   * push(resolve, priority = 0, { reject, isStale })
   * priority: number or () => number (re-read on reprioritize()).
   */
  push(resolve, priority = 0, opts = {}) {
    const node = {
      rank: priority, p: SlotQueue._value(priority), s: this._seq++,
      resolve, reject: opts.reject || null, isStale: opts.isStale || null,
    };
    this._heap.push(node);
    this._up(this._heap.length - 1);
    return this._heap.length;
  }

  // Drop every waiter: the viewer that asked is gone. Waiters that can be
  // rejected get a `cancelled` error; the rest are simply never resolved.
  clear() {
    const nodes = this._heap;
    this._heap = [];
    for (const node of nodes) {
      this.dropped += 1;
      if (node.reject) {
        const err = new Error('cancelled: viewer closed');
        err.cancelled = true;
        try { node.reject(err); } catch (e) { /* nothing to tell */ }
      }
    }
    return nodes.length;
  }

  // The next live waiter's resolve function, or undefined. Stale waiters
  // met on the way are rejected (if they can be) and not counted.
  shift() {
    for (;;) {
      const node = this._pop();
      if (!node) return undefined;
      let stale = false;
      try { stale = !!(node.isStale && node.isStale()); } catch (e) { stale = false; }
      if (!stale) return node.resolve;
      this.dropped += 1;
      if (node.reject) {
        const err = new Error('cancelled: no longer wanted');
        err.cancelled = true;
        node.reject(err);
      }
    }
  }

  // Re-read every function priority and restore heap order.
  reprioritize() {
    const h = this._heap;
    let changed = false;
    for (const node of h) {
      if (typeof node.rank !== 'function') continue;
      const p = SlotQueue._value(node.rank);
      if (p !== node.p) { node.p = p; changed = true; }
    }
    if (!changed) return false;
    for (let i = (h.length >> 1) - 1; i >= 0; i--) this._down(i);
    return true;
  }

  _pop() {
    const h = this._heap;
    if (h.length === 0) return undefined;
    const top = h[0];
    const last = h.pop();
    if (h.length > 0) { h[0] = last; this._down(0); }
    return top;
  }

  _up(i) {
    const h = this._heap;
    while (i > 0) {
      const parent = (i - 1) >> 1;
      if (!SlotQueue._less(h[i], h[parent])) break;
      [h[i], h[parent]] = [h[parent], h[i]];
      i = parent;
    }
  }

  _down(i) {
    const h = this._heap;
    for (;;) {
      const l = 2 * i + 1;
      const r = l + 1;
      let m = i;
      if (l < h.length && SlotQueue._less(h[l], h[m])) m = l;
      if (r < h.length && SlotQueue._less(h[r], h[m])) m = r;
      if (m === i) return;
      [h[i], h[m]] = [h[m], h[i]];
      i = m;
    }
  }

  static _less(a, b) {
    return a.p < b.p || (a.p === b.p && a.s < b.s);
  }
}

/**
 * Several requesters sharing one download (a fragment, a pack, a tile's
 * sidecar): it ranks by the most wanted of them and is stale only when
 * every one of them is. A plain number is a requester that never goes
 * stale. `waiters` is live - requesters added later count.
 */
export function demandOf(waiters) {
  const rankOf = (w) => (typeof w === 'number' ? w : (w && typeof w.rank === 'function' ? w.rank() : (w && Number.isFinite(w.rank) ? w.rank : 0)));
  return {
    rank: () => waiters.reduce((m, w) => Math.min(m, rankOf(w)), Number.MAX_SAFE_INTEGER),
    isStale: () => waiters.every((w) => !!(w && typeof w === 'object' && typeof w.isStale === 'function' && w.isStale())),
  };
}

